import React, { useState } from 'react'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { HStack } from '@astryxdesign/core/HStack'
import PillButton from './PillButton'
import { IconButton } from '@astryxdesign/core/IconButton'
import { MessageSquarePlus, GitCompare } from 'lucide-react'
import { marked } from 'marked'
import { createId } from '../lib/id'
import { renderPlanLineHtml } from '../lib/planMarkdown'
import { buildDiffRenderItems, computeLineDiff, type DiffLine } from '../utils/lineDiff'
import {
  ReviewCommentChip,
  ReviewCommentEditor,
  WHOLE_REVIEW_COMMENT_LINE_INDEX,
  type ReviewComment
} from './ReviewComments'

// The comment shape and its whole-plan sentinel now live in ReviewComments.tsx so the story
// edit modal can share them; re-exported here for the existing importers.
export type PlanComment = ReviewComment
export const WHOLE_PLAN_COMMENT_LINE_INDEX = WHOLE_REVIEW_COMMENT_LINE_INDEX

interface PlanModalProps {
  plan: { summary: string; content: string; comments?: PlanComment[]; isRevision?: boolean }
  previousPlanContent?: string
  // The manuscript passage this plan was started from, when the chat began with a selection.
  // Shown above the plan so the writer can read the beats against the text they replace.
  selectedText?: string
  isReadOnly?: boolean
  onRequestChanges: (comments: PlanComment[]) => void
  onCommentsChange: (comments: PlanComment[]) => void
  onClose: () => void
}

export default function PlanModal({
  plan,
  previousPlanContent,
  selectedText,
  isReadOnly = false,
  onRequestChanges,
  onCommentsChange,
  onClose
}: PlanModalProps): React.JSX.Element {
  const [comments, setComments] = useState<PlanComment[]>(plan.comments || [])
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null)
  const [draftComment, setDraftComment] = useState('')
  const [showDiff, setShowDiff] = useState(false)

  const lines = plan.content.split('\n')
  // A fresh plan (new scene/topic, not a revision of the prior one) has nothing
  // meaningful to diff against — comparing it to an unrelated plan is noise.
  const canShowDiff = Boolean(
    plan.isRevision && previousPlanContent && previousPlanContent !== plan.content
  )
  const diffItems = canShowDiff
    ? buildDiffRenderItems(
        computeLineDiff(previousPlanContent!.split('\n'), plan.content.split('\n'))
      )
    : []

  // A heading/bullet marker ("### ", "- ") is always the leading word token(s) that
  // computeLineDiff's word tokenizer produces, so the word-diff arrays used for a changed line's
  // highlight need the same marker stripped before rendering — otherwise a heading line that
  // changed would show its literal hashes in the diff view even though renderPlanLineHtml strips
  // them everywhere else.
  const stripLeadingMarkerWords = (words: DiffLine[]): DiffLine[] => {
    const [first, ...rest] = words
    if (!first || !/^(#{1,6}|[-*]|\d{1,3}[.)])$/.test(first.text)) return words
    const [second, ...tail] = rest
    return second && /^\s+$/.test(second.text) ? tail : rest
  }

  const renderWordRuns = (
    words: DiffLine[],
    highlightType: 'added' | 'removed'
  ): React.ReactNode => {
    const runs: DiffLine[] = []
    for (const word of stripLeadingMarkerWords(words)) {
      const last = runs[runs.length - 1]
      if (last && last.type === word.type) {
        last.text += word.text
      } else {
        runs.push({ type: word.type, text: word.text })
      }
    }
    return runs.map((run, index) => (
      <span
        key={index}
        className={run.type === highlightType ? `plan-modal-diff-word-${highlightType}` : undefined}
        dangerouslySetInnerHTML={{ __html: marked.parseInline(run.text) as string }}
      />
    ))
  }

  const diffLineClassName = (text: string, base: string): string => {
    const { kind, depth } = renderPlanLineHtml(text)
    if (kind === 'heading') return `${base} plan-modal-heading plan-modal-heading-${depth}`
    if (kind === 'bullet') return `${base} plan-modal-bullet`
    return base
  }

  const commentForLine = (lineIndex: number): PlanComment | undefined =>
    comments.find((comment) => comment.lineIndex === lineIndex)

  const wholePlanComment = commentForLine(WHOLE_PLAN_COMMENT_LINE_INDEX)

  const handleAddComment = (lineIndex: number, lineText: string): void => {
    if (isReadOnly || !draftComment.trim()) return
    const existing = commentForLine(lineIndex)
    const nextComments = existing
      ? comments.map((comment) =>
          comment.lineIndex === lineIndex
            ? { ...comment, comment: draftComment.trim(), resolved: false }
            : comment
        )
      : [...comments, { id: createId(), lineIndex, lineText, comment: draftComment.trim() }]
    setComments(nextComments)
    onCommentsChange(nextComments)
    setDraftComment('')
    setActiveLineIndex(null)
  }

  const handleRemoveComment = (lineIndex: number): void => {
    if (isReadOnly) return
    const nextComments = comments.filter((comment) => comment.lineIndex !== lineIndex)
    setComments(nextComments)
    onCommentsChange(nextComments)
  }

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => !open && onClose()}
      variant="standard"
      purpose="form"
      width="min(80vw, 1000px)"
      maxHeight="80vh"
    >
      <DialogHeader
        title={isReadOnly ? 'Plan (superseded)' : 'Plan'}
        subtitle={plan.summary}
        onOpenChange={() => onClose()}
      />
      <div className="plan-modal-scroll">
        {selectedText && selectedText.trim() && (
          <section className="plan-modal-selection" aria-label="Passage being rewritten">
            <span className="plan-modal-selection-label">Passage being rewritten</span>
            <p className="plan-modal-selection-text">{selectedText}</p>
          </section>
        )}
        <div className="plan-modal-container">
          {showDiff ? (
            <div className="plan-modal-diff">
              {(() => {
                // Two independent ordinal counters — one per side of the diff — so each side
                // numbers its own numbered-beat lines from its own position, self-healing any
                // duplicate/out-of-order digit the model typed. A heading resets whichever
                // side(s) it appears on. A 'same' line exists on both sides, so it advances
                // both counters (and displays the new-side number); otherwise the old side
                // would fall behind and a later removed line would show a stale number.
                let oldCounter = 0
                let newCounter = 0
                return diffItems.map((item, index) => {
                  if (item.kind === 'modified') {
                    if (!item.oldText.trim() && !item.newText.trim()) {
                      return <div key={index} className="plan-modal-blank-line" />
                    }
                    const oldKind = renderPlanLineHtml(item.oldText).kind
                    const newKind = renderPlanLineHtml(item.newText).kind
                    if (oldKind === 'heading') oldCounter = 0
                    if (newKind === 'heading') newCounter = 0
                    const oldNumber = oldKind === 'numbered' ? ++oldCounter : null
                    const newNumber = newKind === 'numbered' ? ++newCounter : null
                    return (
                      <div key={index} className="plan-modal-diff-modified-pair">
                        <div
                          className={diffLineClassName(
                            item.oldText,
                            'plan-modal-diff-line plan-modal-diff-line-modified-old'
                          )}
                        >
                          {oldNumber !== null && (
                            <span className="plan-modal-line-number">{oldNumber}.</span>
                          )}
                          {renderWordRuns(item.oldWords, 'removed')}
                        </div>
                        <div
                          className={diffLineClassName(
                            item.newText,
                            'plan-modal-diff-line plan-modal-diff-line-modified-new'
                          )}
                        >
                          {newNumber !== null && (
                            <span className="plan-modal-line-number">{newNumber}.</span>
                          )}
                          {renderWordRuns(item.newWords, 'added')}
                        </div>
                      </div>
                    )
                  }
                  if (!item.text.trim())
                    return <div key={index} className="plan-modal-blank-line" />
                  const rendered = renderPlanLineHtml(item.text)
                  // Removed lines touch only the old-side counter, added lines only the
                  // new-side counter, and same lines both (displaying the new-side number).
                  const onOldSide = item.kind !== 'added'
                  const onNewSide = item.kind !== 'removed'
                  if (rendered.kind === 'heading') {
                    if (onOldSide) oldCounter = 0
                    if (onNewSide) newCounter = 0
                  }
                  let displayNumber: number | null = null
                  if (rendered.kind === 'numbered') {
                    if (onOldSide) displayNumber = ++oldCounter
                    if (onNewSide) displayNumber = ++newCounter
                  }
                  return (
                    <div
                      key={index}
                      className={diffLineClassName(
                        item.text,
                        `plan-modal-diff-line plan-modal-diff-line-${item.kind}`
                      )}
                    >
                      {displayNumber !== null && (
                        <span className="plan-modal-line-number">{displayNumber}.</span>
                      )}
                      <span dangerouslySetInnerHTML={{ __html: rendered.html }} />
                    </div>
                  )
                })
              })()}
            </div>
          ) : (
            <div className="plan-modal-lines">
              {(!isReadOnly || wholePlanComment) && (
                <div className="plan-modal-whole-plan-row">
                  {!isReadOnly &&
                    (wholePlanComment ? (
                      <PillButton
                        variant="ghost"
                        size="sm"
                        label="Edit"
                        endContent={<MessageSquarePlus size={12} />}
                        tooltip="Edit comment"
                        onClick={() => {
                          setActiveLineIndex(WHOLE_PLAN_COMMENT_LINE_INDEX)
                          setDraftComment(wholePlanComment.comment)
                        }}
                        className="plan-modal-comment-trigger plan-modal-comment-trigger-active"
                      />
                    ) : (
                      activeLineIndex !== WHOLE_PLAN_COMMENT_LINE_INDEX && (
                        <PillButton
                          variant="ghost"
                          size="sm"
                          label="Comment on the whole plan"
                          icon={<MessageSquarePlus size={12} />}
                          tooltip="Comment on the whole plan"
                          onClick={() => {
                            setActiveLineIndex(WHOLE_PLAN_COMMENT_LINE_INDEX)
                            setDraftComment('')
                          }}
                          className="plan-modal-whole-plan-trigger"
                        />
                      )
                    ))}
                  {wholePlanComment && activeLineIndex !== WHOLE_PLAN_COMMENT_LINE_INDEX && (
                    <ReviewCommentChip
                      comment={wholePlanComment}
                      isReadOnly={isReadOnly}
                      onEdit={() => {
                        setActiveLineIndex(WHOLE_PLAN_COMMENT_LINE_INDEX)
                        setDraftComment(wholePlanComment.comment)
                      }}
                      onRemove={() => handleRemoveComment(WHOLE_PLAN_COMMENT_LINE_INDEX)}
                      className="plan-modal-whole-plan-chip"
                    />
                  )}
                  {!isReadOnly && activeLineIndex === WHOLE_PLAN_COMMENT_LINE_INDEX && (
                    <ReviewCommentEditor
                      value={draftComment}
                      onChange={setDraftComment}
                      placeholder="What should change about the plan overall?"
                      onSave={() => handleAddComment(WHOLE_PLAN_COMMENT_LINE_INDEX, '')}
                      onCancel={() => {
                        setActiveLineIndex(null)
                        setDraftComment('')
                      }}
                    />
                  )}
                </div>
              )}
              {(() => {
                // Ordinal counter derived from position, not the digit typed in the plan's raw
                // content, so a duplicate/out-of-order number ("1., 2., 2., 3.") self-heals in
                // the display. A heading restarts the run.
                let lineNumberCounter = 0
                return lines.map((line, index) => {
                  if (!line.trim()) return <div key={index} className="plan-modal-blank-line" />
                  const comment = commentForLine(index)
                  const rendered = renderPlanLineHtml(line)
                  if (rendered.kind === 'heading') lineNumberCounter = 0
                  const displayNumber = rendered.kind === 'numbered' ? ++lineNumberCounter : null
                  const lineTextClassName =
                    rendered.kind === 'heading'
                      ? `plan-modal-line-text plan-modal-heading plan-modal-heading-${rendered.depth}`
                      : rendered.kind === 'bullet'
                        ? 'plan-modal-line-text plan-modal-bullet'
                        : 'plan-modal-line-text'
                  return (
                    <div
                      key={index}
                      className={
                        displayNumber !== null
                          ? 'plan-modal-line-row plan-modal-line-row-numbered'
                          : 'plan-modal-line-row'
                      }
                    >
                      <div className="plan-modal-line-header">
                        {displayNumber !== null && (
                          <span className="plan-modal-line-number">{displayNumber}.</span>
                        )}
                        <div
                          className={lineTextClassName}
                          dangerouslySetInnerHTML={{ __html: rendered.html }}
                        />
                        {!isReadOnly &&
                          (comment ? (
                            <PillButton
                              variant="ghost"
                              size="sm"
                              label="Edit"
                              endContent={<MessageSquarePlus size={12} />}
                              tooltip="Edit comment"
                              onClick={() => {
                                setActiveLineIndex(index)
                                setDraftComment(comment.comment)
                              }}
                              className="plan-modal-comment-trigger plan-modal-comment-trigger-active"
                            />
                          ) : (
                            <IconButton
                              variant="ghost"
                              size="sm"
                              icon={<MessageSquarePlus size={12} />}
                              label="Comment on this line"
                              tooltip="Comment on this line"
                              onClick={() => {
                                setActiveLineIndex(index)
                                setDraftComment('')
                              }}
                              className="plan-modal-comment-trigger"
                            />
                          ))}
                      </div>
                      {comment && activeLineIndex !== index && (
                        <ReviewCommentChip
                          comment={comment}
                          isReadOnly={isReadOnly}
                          onEdit={() => {
                            setActiveLineIndex(index)
                            setDraftComment(comment.comment)
                          }}
                          onRemove={() => handleRemoveComment(index)}
                        />
                      )}
                      {!isReadOnly && activeLineIndex === index && (
                        <ReviewCommentEditor
                          value={draftComment}
                          onChange={setDraftComment}
                          placeholder="What should change about this line?"
                          onSave={() => handleAddComment(index, line)}
                          onCancel={() => {
                            setActiveLineIndex(null)
                            setDraftComment('')
                          }}
                        />
                      )}
                    </div>
                  )
                })
              })()}
            </div>
          )}
        </div>
      </div>
      <div className="plan-modal-footer">
        <HStack gap={1} justify="between">
          <div>
            {canShowDiff && (
              <PillButton
                variant={showDiff ? 'secondary' : 'ghost'}
                size="sm"
                label={showDiff ? 'Hide Diff' : 'Show Diff'}
                icon={<GitCompare size={12} />}
                onClick={() => setShowDiff((value) => !value)}
              />
            )}
          </div>
          <HStack gap={1} justify="end">
            <PillButton variant="ghost" label="Close" onClick={onClose} />
            {!isReadOnly && (
              <PillButton
                variant="primary"
                label="Request Changes"
                isDisabled={comments.every((comment) => comment.resolved)}
                onClick={() => onRequestChanges(comments)}
              />
            )}
          </HStack>
        </HStack>
      </div>
    </Dialog>
  )
}
